import { Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

export type Language = 'en';

export interface LanguageOption {
  code: Language;
  name: string;
  nativeName: string;
}

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private readonly STORAGE_KEY = 'pushub-language';
  
  readonly availableLanguages: LanguageOption[] = [
    { code: 'en', name: 'English', nativeName: 'English' }
  ];

  readonly currentLanguage = signal<Language>('en');

  constructor(private translate: TranslateService) {
    this.initializeLanguage();
  }

  private initializeLanguage(): void {
    // Set available languages
    this.translate.addLangs(this.availableLanguages.map(lang => lang.code));

    // Set default language
    this.translate.setDefaultLang('en');

    // Try to get saved language from localStorage
    const savedLanguage = this.getSavedLanguage();
    
    // Detect browser language if no saved language
    const browserLang = this.translate.getBrowserLang();
    const detectedLang = browserLang && this.availableLanguages.some(lang => lang.code === browserLang)
      ? browserLang as Language
      : 'en';

    // Use saved language or detected language
    const languageToUse = savedLanguage || detectedLang;
    
    this.setLanguage(languageToUse);
  }

  setLanguage(language: Language): void {
    if (this.availableLanguages.some(lang => lang.code === language)) {
      this.translate.use(language);
      this.currentLanguage.set(language);
      this.saveLanguage(language);
      
      // Update HTML lang attribute for accessibility
      document.documentElement.lang = language;
    }
  }

  toggleLanguage(): void {
    const currentIndex = this.availableLanguages.findIndex(
      lang => lang.code === this.currentLanguage()
    );
    const nextIndex = (currentIndex + 1) % this.availableLanguages.length;
    this.setLanguage(this.availableLanguages[nextIndex].code);
  }

  private getSavedLanguage(): Language | null {
    if (typeof localStorage !== 'undefined') {
      const saved = localStorage.getItem(this.STORAGE_KEY);
      return saved && this.availableLanguages.some(lang => lang.code === saved)
        ? saved as Language
        : null;
    }
    return null;
  }

  private saveLanguage(language: Language): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(this.STORAGE_KEY, language);
    }
  }

  getCurrentLanguageOption(): LanguageOption {
    return this.availableLanguages.find(lang => lang.code === this.currentLanguage())
      || this.availableLanguages[0];
  }
}

